import { useState, useMemo, useCallback, memo, useEffect } from "react";
import { Outlet, useLocation, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useFormContext, useWatch } from "react-hook-form";
import { Box, Button, CircularProgress, Grid, Skeleton } from "@mui/material";
import FormWrapper from "./components/common/FormWrapper";
import { SectionSchema } from "./utils/schemas/initial";
import FbPropertyTab from "./components/features/FbPropertyTab";
import FbSectionPropertyTab from "./components/features/FbSectionPropertyTab";
import { validationSchema } from "./utils/schemas/validation";
import FbLayout from "./layout/fbLayout";
import FormContent from "./FormContent";
import {
  fetchSections,
  saveAsDraft,
  sectionAutoSave,
  setFormData,
  submitSection,
} from "./slices/FbSections";
import {
  fetchTemplates,
  updateTemplateStatus,
} from "../../bmr/slices/formTemplate";
import { fetchInputTypes, getValidationOptions } from "./slices/FbProperty";
import BreadCrumb from "../../pages/breadcrumb/BreadCrumb";
import { fetchAlllabel } from "../../bmr/slices/formbuildermaster";
import { limit, page } from "./components/constant/constant";

const useFormState = () => {
  const [loading, setLoading] = useState(false);
  const FORM_SCHEMA_UPDATED = useSelector(
    (state) => state?.bmrFbSections?.formData?.sections,
  );

  const result = FORM_SCHEMA_UPDATED.map((sec) => ({
  ...sec,
  fields: [...(sec?.fields || [])].sort(
    (a, b) => (a?.order || 0) - (b?.order || 0)
  ),
}));
  const FORM_SCHEMA = { sections: structuredClone(result || []) };
  const [selectedField, setSelectedField] = useState("");
  const [selectedSection, setSelectedSection] = useState(
    FORM_SCHEMA?.sections[0]?.section_uuid,
  );
  const [submitLoading, setSubmitLoading] = useState(false);
  const [draftLoading, setDraftLoading] = useState(false);
  const [isSectionAdd, setIsSectionAdd] = useState(false);
  const [isFieldAdd, setIsFieldAdd] = useState(false);
  const { id } = useParams();
  const dispatch = useDispatch();

  const onSubmit = useCallback(async () => {
    try {
       setSubmitLoading(true);
      const response = await dispatch(submitSection(id)).unwrap();
      await dispatch(
        updateTemplateStatus({ id, status: response.data.status }),
      );
      dispatch(
        fetchTemplates({
          page: 1,
          per_page: 12,
          search: null,
          status: "",
          version: "",
        }),
      );
    } catch (error) {
      console.error(error);
    } finally {
      setSubmitLoading(false);
      // setLoading(false);
    }
  }, [dispatch, id]);

  const handleFetchData = async (tempId) => {
    try {
      setLoading(true);
      await dispatch(fetchInputTypes());
      await dispatch(
        fetchAlllabel({
          page: page,
          limit: limit,
        }),
      );
      const response = await dispatch(fetchSections(tempId)).unwrap();
      await dispatch(getValidationOptions());
      if (response.data.sections.length > 0) {
        return await dispatch(
          setFormData({ sections: response.data.sections }),
        );
      } else {
        return await dispatch(setFormData({ sections: [SectionSchema] }));
      }
    } catch (error) {
      setLoading(false);
      console.error(error?.message || error?.data?.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    handleFetchData(id);
  }, [id]);

  return {
    loading,
    setLoading,
    selectedSection,
    selectedField,
    setSelectedSection,
    setSelectedField,
    onSubmit,
    FORM_SCHEMA,
    FORM_SCHEMA_UPDATED,
    isFieldAdd,
    setIsFieldAdd,
    isSectionAdd,
    setIsSectionAdd,
    submitLoading,
    setSubmitLoading,
    draftLoading,
    setDraftLoading,
  };
};

// Separate component for property panel that changes frequently
const DynamicPropertyPanel = memo(({ selectedSection, selectedField }) => {
  const { control } = useFormContext();
  const sectionSchemas = useWatch({
    control,
    name: `sections`,
  });
  const sectionIndex = sectionSchemas?.findIndex(
    (section) => section?.section_uuid === selectedSection
  );
  const fieldIndex = sectionSchemas[sectionIndex]?.fields?.findIndex(
    (field) => field?.input_uuid === selectedField
  );
  return (
    <>
      {selectedSection && selectedField ? (
        <FbPropertyTab
          key={`property-${selectedSection}-${selectedField}`}
          selectedSection={selectedSection}
          selectedField={selectedField}
          sectionIndex={sectionIndex >= 0 ? sectionIndex : 0}
          fieldIndex={fieldIndex >= 0 ? fieldIndex : 0}
        />
      ) : (
        <FbSectionPropertyTab selectedSection={selectedSection} />
      )}
    </>
  );
});

// Static form footer (should not re-render often)
const StaticFormFooter = memo(({ loading, isPublished, submitLoading,
    draftLoading,
    setDraftLoading, }) => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const { getValues } = useFormContext();
 
  const handleSaveAsDraft = async () => {
  try {
    setDraftLoading(true);

    const updatedSections = getValues("sections");
    const clonedSections = structuredClone(updatedSections);

    await dispatch(
      sectionAutoSave({
        id,
        data: { sections: clonedSections },
      })
    );

    const response = await dispatch(saveAsDraft(id)).unwrap();

    await dispatch(
      updateTemplateStatus({
        id,
        status: response.data.status,
      })
    );

    dispatch(
      fetchTemplates({
        page: 1,
        per_page: 12,
        search: null,
        status: "",
        version: "",
      })
    );
  } finally {
    setDraftLoading(false);
  }
};
  return (
    <Box className="bg-white px-4 py-3 rounded-xl w-full flex justify-end gap-2 h-[72px] border-t border-gray-200">
      <Button
        variant="outlined"
        disabled={loading || isPublished || draftLoading}
        onClick={handleSaveAsDraft}
        sx={{
          borderRadius: "0.375rem !important",
          opacity: 1,
        }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <span>Save as draft</span>
          {draftLoading && <CircularProgress size={16} />}
        </Box>
      </Button>

      <Button
        type="submit"
        variant="contained"
        disabled={loading || isPublished || submitLoading}
        sx={{
          borderRadius: "0.375rem !important",
          width: "7.18rem !important",
          opacity: 1,
        }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <span>Submit</span>
          {submitLoading && <CircularProgress size={16} color="inherit" />}
        </Box>
      </Button>
    </Box>
  );
});

// Main FormBuilder component - this is now truly static
const FormBuilder = memo(() => {
  const location = useLocation();

  // This is the key: path rarely changes, so this component stays stable
  const path = useMemo(
    () => location?.pathname?.split("/")[4],
    [location?.pathname]
  );

  return <StaticFormBuilderContent path={path} />;
});

// Static content component
const StaticFormBuilderContent = memo(({ path }) => {
  return (
    <Box className="p-2">
      <FormBuilderWithState path={path} />
    </Box>
  );
});

// Component that manages state but delegates rendering
const FormBuilderWithState = memo(({ path }) => {
  const location = useLocation();
  const { id } = useParams();

  // Compute breadcrumbs - use location.state if available, otherwise generate from path
  const breadCrumbArray = useMemo(() => {
    if (location?.state?.length > 0) {
      return location.state;
    }
    // Fallback: generate breadcrumbs from path
    const segments = location.pathname.split("/").filter(Boolean);
    if (segments.includes("form-management")) {
      return [
        { name: "Form Management", path: "/bmr/form-management", isBack: true },
        { name: "Template", path: location.pathname },
      ];
    }
    return [];
  }, [location.pathname, location.state]);

  const templateDetails = useSelector(
    (state) => state?.bmrFbl?.fbTemplate?.items || []
  );
  const currentFormTemplate = templateDetails.find(
    (template) => String(template?.id) === String(id)
  );
  const isPublished = currentFormTemplate?.status === 2;

  const formState = useFormState();
  const {
    loading,
    selectedSection,
    selectedField,
    FORM_SCHEMA,
    setSelectedSection,
    setSelectedField,
    isFieldAdd,
    setIsFieldAdd,
    isSectionAdd,
    setIsSectionAdd,
    onSubmit,
    setDraftLoading,
    draftLoading,
    submitLoading
  } = formState;
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isValidateAndSubmitOpen, setIsValidateAndSubmitOpen] = useState(false);

  return (
    <>
      <div className="mb-2">
        <BreadCrumb BreadCrumbsListing={breadCrumbArray} />
      </div>
      {loading ? (
        <Grid container columns={10} spacing={2} className="h-screen">
          <Grid item xs={12} md={2} className="h-screen">
            <Skeleton animation="wave" variant="rounded" height="40vh" />
          </Grid>
          <Grid item xs={12} md={8}>
            <Grid container columns={8} spacing={2} className="h-full">
              <Grid item xs={12} md={6}>
                <Skeleton animation="wave" variant="rounded" height="40vh" />
              </Grid>
              <Grid item xs={12} md={2}>
                <Skeleton animation="wave" variant="rounded" height="40vh" />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      ) : (
        <FormWrapper
          onSubmit={onSubmit}
          validationSchema={validationSchema}
          defaultValues={FORM_SCHEMA}
          loading={loading}
        >
          <Grid container columns={10} spacing={2} className="h-screen">
            <Grid item xs={12} md={2}>
              <FbLayout
                selectedSection={selectedSection}
                selectedField={selectedField}
                setSelectedSection={setSelectedSection}
                setSelectedField={setSelectedField}
                isPreviewOpen={isPreviewOpen}
                setIsPreviewOpen={setIsPreviewOpen}
                isValidateAndSubmitOpen={isValidateAndSubmitOpen}
                setIsValidateAndSubmitOpen={setIsValidateAndSubmitOpen}
              />
            </Grid>
            <Grid item xs={12} md={8}>
              <Box component="main">
                {!path ? (
                  <Box className="overflow-hidden">
                    <Grid
                      container
                      columns={8}
                      spacing={2}
                      className="h-full overflow-hidden"
                    >
                      <Grid item xs={12} md={6}>
                        <Box className="h-full flex flex-col overflow-hidden">
                          <Box
                            className="overflow-y-auto"
                            style={{ height: "calc(100vh - 90px)" }}
                          >
                            <FormContent
                              SectionSchema={SectionSchema}
                              selectedSection={selectedSection}
                              selectedField={selectedField}
                              setSelectedSection={setSelectedSection}
                              setSelectedField={setSelectedField}
                              isSectionAdd={isSectionAdd}
                              setIsSectionAdd={setIsSectionAdd}
                              isFieldAdd={isFieldAdd}
                              setIsFieldAdd={setIsFieldAdd}
                              isPreviewOpen={isPreviewOpen}
                            />
                          </Box>
                          <StaticFormFooter
                            loading={loading}
                            isPublished={isPublished}
                            submitLoading={submitLoading}
                            draftLoading={draftLoading}
                            setDraftLoading={setDraftLoading}
                          />
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={2} className="h-screen">
                        <DynamicPropertyPanel
                          selectedSection={selectedSection}
                          selectedField={selectedField}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                ) : (
                  <Box className="overflow-y-auto">
                    <Outlet />
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>
        </FormWrapper>
      )}
    </>
  );
});

FormBuilder.displayName = "FormBuilder";

export default FormBuilder;
